import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import {
  School as StudentIcon,
  Assignment as ApplicationIcon,
  FormatListBulleted as MyRequestIcon,
  Route as RouteIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import TransportApplication from "./Tabs/TransportForm/index";
import RouteManagementTabs from "./Tabs/RouteManagementTabs";
import StudentDetailsTab from "./Tabs/StudentDetailsTab";
import StudentManagement from "./Tabs/StudentManagement";
import { jwtDecode } from "jwt-decode";

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("student-details");
  const [userRole, setUserRole] = useState(10);

  const token = localStorage.getItem("token");
  const userEmail = localStorage.getItem("userEmail") || "";
  const hasStudents = localStorage.getItem("hasStudents");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");

    if (tabParam === "student-details") {
      setActiveSection("student-details");
    } else if (tabParam === "transport") {
      setActiveSection("transport");
    } else if (tabParam === "my-request") {
      setActiveSection("my-request");
    } else if (hasStudents === "false") {
      setActiveSection("student-details");
    } else {
      setActiveSection("transport");
    }
  }, [location.search, hasStudents]);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [token]);

  const handleMenuItemClick = (section) => {
    setActiveSection(section);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box sx={{ display: "flex", gap: 2, minHeight: "80vh" }}>
        {/* Compact, Fixed Left Sidebar Menu moved close to left edge */}
        <Paper
          elevation={2}
          sx={{
            width: 210,
            flexShrink: 0,
            borderRadius: 0,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            justify: "space-between",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Box sx={{ px: 1, py: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parent Portal
              </Typography>
              {userEmail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-all",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  <PersonIcon fontSize="inherit" color="action" /> {userEmail}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {/* Student Details Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("student-details")}
                selected={activeSection === "student-details"}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StudentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Student Details"
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: activeSection === "student-details" ? 600 : 400,
                  }}
                />
              </ListItem>

              {/* Application Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("transport")}
                selected={activeSection === "transport"}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ApplicationIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Application"
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: activeSection === "transport" ? 600 : 400,
                  }}
                />
              </ListItem>

              {/* My Request Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("my-request")}
                selected={activeSection === "my-request"}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MyRequestIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="My Request"
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: activeSection === "my-request" ? 600 : 400,
                  }}
                />
              </ListItem>

              {/* Route Management (If Admin) */}
              {userRole < 2 && (
                <ListItem
                  button
                  onClick={() => handleMenuItemClick("route")}
                  selected={activeSection === "route"}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    px: 1.5,
                    "&.Mui-selected": {
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      "& .MuiListItemIcon-root": { color: "#1976d2" },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <RouteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Route Manage"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: activeSection === "route" ? 600 : 400,
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Paper>

        {/* Maximized Right Main Content Panel */}
        <Paper
          elevation={1}
          sx={{
            flexGrow: 1,
            borderRadius: 0,
            p: 2.5,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            minHeight: "80vh",
            overflowX: "hidden",
          }}
        >
          {activeSection === "student-details" && (
            <StudentDetailsTab
              parentEmail={userEmail}
              onStudentAdded={() => {
                // Optional callback
              }}
            />
          )}

          {activeSection === "transport" && <TransportApplication />}

          {activeSection === "my-request" && <StudentManagement />}

          {activeSection === "route" && <RouteManagementTabs />}
        </Paper>
      </Box>
    </Box>
  );
};

export default Index;
